import React, { useState } from 'react';

interface CoverVariant {
  titleLines: [string, string];
  author: string;
  theme: 'minimalistic' | 'geometric' | 'watercolor' | 'vintage' | 'cinematic';
  front1: string;
  front2: string;
  back: string;
  spine: string;
  textColor: string;
  onLight: boolean;
  showGlow: boolean;
  glow: string;
  glowCx: number;
  glowCy: number;
  glowR: number;
  grainOpacity: number;
}

// Illustrative examples only — not the author's real book. Each one maps to a
// real style category from the AI cover generator (minimalistic, geometric,
// watercolor, vintage, cinematic) so the range shown here isn't invented —
// it's what the tool can actually produce.
const COVER_VARIANTS: CoverVariant[] = [
  {
    titleLines: ['The Long', 'Way Home'], author: 'N. ALVAREZ', theme: 'minimalistic',
    front1: '#f4efe6', front2: '#f4efe6', back: '#eae3d4', spine: '#ddd4bd',
    textColor: '#2b2620', onLight: true, showGlow: false,
    glow: '#000000', glowCx: 0, glowCy: 0, glowR: 0, grainOpacity: 0.03,
  },
  {
    titleLines: ['Angles', 'of Light'], author: 'K. FENWICK', theme: 'geometric',
    front1: '#1c2b4a', front2: '#1c2b4a', back: '#141f36', spine: '#0f1728',
    textColor: '#ffffff', onLight: false, showGlow: false,
    glow: '#000000', glowCx: 0, glowCy: 0, glowR: 0, grainOpacity: 0.05,
  },
  {
    titleLines: ['Soft', 'Horizons'], author: 'M. DUBOIS', theme: 'watercolor',
    front1: '#f6e3c9', front2: '#e6d3ea', back: '#efe4ea', spine: '#e3d3de',
    textColor: '#4a3350', onLight: true, showGlow: false,
    glow: '#000000', glowCx: 0, glowCy: 0, glowR: 0, grainOpacity: 0.015,
  },
  {
    titleLines: ['The Almanac', 'of Roads'], author: 'E. WHITFIELD', theme: 'vintage',
    front1: '#d8c9a2', front2: '#d8c9a2', back: '#cdbb92', spine: '#b9a476',
    textColor: '#3d2b1a', onLight: true, showGlow: false,
    glow: '#000000', glowCx: 0, glowCy: 0, glowR: 0, grainOpacity: 0.14,
  },
  {
    titleLines: ['Last Light', 'Overland'], author: 'A. MERIDIAN', theme: 'cinematic',
    front1: '#14181f', front2: '#05070a', back: '#0d0f13', spine: '#030405',
    textColor: '#f3ede4', onLight: false, showGlow: true,
    glow: '#f2b26b', glowCx: 380, glowCy: 70, glowR: 100, grainOpacity: 0.06,
  },
];

const STATIC_HERO_POINTS = [
  'Front, back & spine as one flat-lay file',
  'No extra cost',
];

interface CoverDesignerHeroProps {
  onStartDesigning: () => void;
  hasExistingCoverDesign: boolean;
  isCoverFromDesigner: boolean;
  spineWidthInches: number;
  savedCoverPreviewUrl?: string | null;
}

const CoverDesignerHero: React.FC<CoverDesignerHeroProps> = ({
  onStartDesigning,
  hasExistingCoverDesign,
  isCoverFromDesigner,
  spineWidthInches,
  savedCoverPreviewUrl,
}) => {
  const [variantIndex, setVariantIndex] = useState(0);
  const variant = COVER_VARIANTS[variantIndex];
  const cycleVariant = () => setVariantIndex((i) => (i + 1) % COVER_VARIANTS.length);
  const heroPoints = [`Correct spine width, every time — ${spineWidthInches}″ for this book`, ...STATIC_HERO_POINTS];
  // Show the real cover only when it's actually the Designer's own output —
  // hasExistingCoverDesign alone just means a canvas was saved at some point,
  // which stays true even after a later direct upload replaces the active
  // file, and would otherwise show someone else's file here as if it were this.
  const showSavedCover = isCoverFromDesigner && !!savedCoverPreviewUrl;

  return (
    <div className="relative bg-gradient-to-b from-orange-50 to-orange-50/40 border-[1.5px] border-orange-200 rounded-2xl p-8 mb-6">
      <span className="absolute -top-2.5 left-8 bg-white text-orange-600 border border-orange-300 text-[10.5px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
        Free · Recommended
      </span>

      {/* Illustration + cycle dots — big and full-width (capped), the
          persuasive "hero" visual, not a small supporting icon. A white mat
          frames it so it separates cleanly from the orange backdrop no
          matter which variant's own palette is showing (light or dark). */}
      <div className="relative max-w-[416px] mx-auto mb-6">
        {showSavedCover ? (
          <button
            type="button"
            onClick={onStartDesigning}
            title="Click to continue designing your cover"
            aria-label="Continue designing your saved cover"
            className="relative block w-full aspect-[480/360] rounded-lg overflow-hidden bg-white p-2 shadow-[0_5px_10px_rgba(0,0,0,0.14)] hover:shadow-[0_6px_12px_rgba(0,0,0,0.18)] hover:-translate-y-px transition-all"
          >
            <div className="relative w-full h-full rounded-[3px] overflow-hidden bg-gray-50">
              <img src={savedCoverPreviewUrl!} alt="Your saved cover design" className="absolute inset-0 w-full h-full object-contain" />
            </div>
          </button>
        ) : (
        <button
          type="button"
          onClick={cycleVariant}
          title="Click to see another example"
          aria-label="Preview another example cover design"
          className="relative block w-full aspect-[480/360] rounded-lg overflow-hidden bg-white p-2 shadow-[0_5px_10px_rgba(0,0,0,0.14)] hover:shadow-[0_6px_12px_rgba(0,0,0,0.18)] hover:-translate-y-px transition-all"
          style={{ containerType: 'inline-size' } as React.CSSProperties}
        >
          <div className="relative w-full h-full rounded-[3px] overflow-hidden">
            <svg viewBox="0 0 480 360" preserveAspectRatio="none" className="absolute inset-0 w-full h-full block">
              <defs>
                <linearGradient id="cdhFrontGrad" x1="0.1" y1="0" x2="0.9" y2="1">
                  <stop offset="0%" stopColor={variant.front1} />
                  <stop offset="100%" stopColor={variant.front2} />
                </linearGradient>
                <radialGradient id="cdhGlow" cx="0.5" cy="0.24" r="0.5">
                  <stop offset="0%" stopColor={variant.glow} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={variant.glow} stopOpacity={0} />
                </radialGradient>
                <radialGradient id="cdhVignette" cx="0.5" cy="0.46" r="0.72">
                  <stop offset="60%" stopColor="#000000" stopOpacity={0} />
                  <stop offset="100%" stopColor="#000000" stopOpacity={0.32} />
                </radialGradient>
                <clipPath id="cdhFrontClip"><rect x="252" y="0" width="228" height="360" /></clipPath>
                <filter id="cdhGrain">
                  <feTurbulence type="fractalNoise" baseFrequency={0.85} numOctaves={2} stitchTiles="stitch" result="noise" />
                  <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.5 0" />
                </filter>
                <filter id="cdhBlur"><feGaussianBlur stdDeviation="7" /></filter>
                <filter id="cdhPetalBlur"><feGaussianBlur stdDeviation="1.3" /></filter>
              </defs>

              {/* back cover */}
              <rect x="0" y="0" width="228" height="360" fill={variant.back} />
              {/* spine */}
              <rect x="228" y="0" width="24" height="360" fill={variant.spine} />
              {/* front cover */}
              <rect x="252" y="0" width="228" height="360" fill="url(#cdhFrontGrad)" />
              {variant.showGlow && <circle cx={variant.glowCx} cy={variant.glowCy} r={variant.glowR} fill="url(#cdhGlow)" />}

              {variant.theme === 'minimalistic' && (
                <g clipPath="url(#cdhFrontClip)">
                  {/* one simple line-drawn object as the single focal element,
                      the classic minimalist-cover move — an icon + type, not
                      an abstract shape */}
                  <circle cx="304" cy="298" r="28" fill="none" stroke="#c1673f" strokeWidth={3} />
                  <circle cx="416" cy="298" r="28" fill="none" stroke="#c1673f" strokeWidth={3} />
                  <circle cx="304" cy="298" r="2.5" fill="#c1673f" />
                  <circle cx="416" cy="298" r="2.5" fill="#c1673f" />
                  <circle cx="352" cy="298" r="3" fill="#c1673f" />
                  <path
                    d="M304,298 L352,298 L338,254 L392,254 L416,298 M352,298 L392,254"
                    fill="none" stroke="#c1673f" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
                  />
                  <line x1="331" y1="250" x2="346" y2="250" stroke="#c1673f" strokeWidth={4} strokeLinecap="round" />
                  <line x1="388" y1="246" x2="400" y2="242" stroke="#c1673f" strokeWidth={3} strokeLinecap="round" />
                </g>
              )}
              {variant.theme === 'geometric' && (
                <g clipPath="url(#cdhFrontClip)">
                  <polygon points="252,360 388,168 480,360" fill="#e8ac41" opacity={0.85} />
                  <polygon points="330,360 452,222 480,360" fill="#e2643f" opacity={0.75} />
                  <line x1="252" y1="200" x2="480" y2="200" stroke="#f3ede4" strokeOpacity={0.12} strokeWidth={1} />
                </g>
              )}
              {variant.theme === 'watercolor' && (
                <g clipPath="url(#cdhFrontClip)">
                  {/* soft atmospheric washes, background layer */}
                  <g filter="url(#cdhBlur)">
                    <ellipse cx="430" cy="90" rx="70" ry="55" fill="#e0a63c" opacity={0.2} />
                    <ellipse cx="270" cy="280" rx="80" ry="60" fill="#5f6fc0" opacity={0.16} />
                  </g>
                  {/* a real subject — a loose botanical spray, not abstract washes */}
                  <g filter="url(#cdhPetalBlur)">
                    <path d="M266,352 Q288,300 300,272 T336,214" fill="none" stroke="#6a9b7a" strokeOpacity={0.55} strokeWidth={2} />
                    <ellipse cx="284" cy="318" rx="12" ry="5" fill="#6a9b7a" opacity={0.45} transform="rotate(-35 284 318)" />
                    <ellipse cx="312" cy="248" rx="11" ry="4.5" fill="#6a9b7a" opacity={0.4} transform="rotate(30 312 248)" />
                    <circle cx="270" cy="332" r="13" fill="#df5f82" opacity={0.55} />
                    <circle cx="280" cy="326" r="11" fill="#e2728f" opacity={0.5} />
                    <circle cx="266" cy="322" r="10" fill="#df5f82" opacity={0.45} />
                    <circle cx="302" cy="270" r="10" fill="#e0a63c" opacity={0.55} />
                    <circle cx="311" cy="264" r="8.5" fill="#e6b95c" opacity={0.5} />
                    <circle cx="336" cy="214" r="8" fill="#df5f82" opacity={0.55} />
                    <circle cx="343" cy="209" r="6.5" fill="#e2728f" opacity={0.5} />
                  </g>
                </g>
              )}
              {variant.theme === 'vintage' && (
                <g clipPath="url(#cdhFrontClip)">
                  <rect x="268" y="20" width="196" height="320" fill="none" stroke="#3d2b1a" strokeOpacity={0.4} strokeWidth={1.5} />
                  <rect x="274" y="26" width="184" height="308" fill="none" stroke="#3d2b1a" strokeOpacity={0.25} strokeWidth={0.75} />
                  {/* corner flourishes */}
                  <path d="M274,42 q14,0 14,14" fill="none" stroke="#3d2b1a" strokeOpacity={0.4} strokeWidth={1} />
                  <path d="M458,42 q-14,0 -14,14" fill="none" stroke="#3d2b1a" strokeOpacity={0.4} strokeWidth={1} />
                  <path d="M274,318 q14,0 14,-14" fill="none" stroke="#3d2b1a" strokeOpacity={0.4} strokeWidth={1} />
                  <path d="M458,318 q-14,0 -14,-14" fill="none" stroke="#3d2b1a" strokeOpacity={0.4} strokeWidth={1} />
                  {/* ornamental rules flanking the title block */}
                  <line x1="292" y1="78" x2="340" y2="78" stroke="#3d2b1a" strokeOpacity={0.45} strokeWidth={1} />
                  <line x1="392" y1="78" x2="440" y2="78" stroke="#3d2b1a" strokeOpacity={0.45} strokeWidth={1} />
                  <circle cx="366" cy="78" r="3" fill="#3d2b1a" opacity={0.5} />
                  <line x1="330" y1="198" x2="402" y2="198" stroke="#3d2b1a" strokeOpacity={0.35} strokeWidth={0.75} />
                  {/* foxing / age spots */}
                  <ellipse cx="292" cy="252" rx="11" ry="8" fill="#7a5a30" opacity={0.14} />
                  <ellipse cx="432" cy="170" rx="9" ry="7" fill="#7a5a30" opacity={0.12} />
                  <ellipse cx="410" cy="290" rx="7" ry="6" fill="#7a5a30" opacity={0.1} />
                </g>
              )}
              {variant.theme === 'cinematic' && (
                <g clipPath="url(#cdhFrontClip)">
                  <rect x="230" y="-40" width="26" height="460" fill="#ffffff" opacity={0.05} transform="rotate(20 366 180)" />
                  <rect x="336" y="-40" width="16" height="460" fill="#ffffff" opacity={0.04} transform="rotate(20 366 180)" />
                  <path d="M252,340 L480,300" stroke="#000000" strokeOpacity={0.22} strokeWidth={40} />
                </g>
              )}

              {!variant.onLight && <rect x="252" y="0" width="228" height="360" fill="url(#cdhVignette)" />}

              {/* back-cover blurb lines */}
              <rect x="24" y="140" width="130" height="4" rx="2" fill={variant.onLight ? '#2b2620' : '#ffffff'} opacity={variant.onLight ? 0.16 : 0.22} />
              <rect x="24" y="156" width="168" height="4" rx="2" fill={variant.onLight ? '#2b2620' : '#ffffff'} opacity={variant.onLight ? 0.16 : 0.22} />
              <rect x="24" y="172" width="112" height="4" rx="2" fill={variant.onLight ? '#2b2620' : '#ffffff'} opacity={variant.onLight ? 0.16 : 0.22} />
              <rect x="24" y="188" width="150" height="4" rx="2" fill={variant.onLight ? '#2b2620' : '#ffffff'} opacity={variant.onLight ? 0.16 : 0.22} />
              {/* barcode */}
              <rect x="24" y="300" width="48" height="28" fill="#f3ede4" opacity={0.92} />
              <rect x="29" y="304" width="1.4" height="20" fill="#2a2a2a" />
              <rect x="32" y="304" width="0.8" height="20" fill="#2a2a2a" />
              <rect x="35" y="304" width="2" height="20" fill="#2a2a2a" />
              <rect x="39" y="304" width="1" height="20" fill="#2a2a2a" />
              <rect x="42" y="304" width="1.6" height="20" fill="#2a2a2a" />
              <rect x="46" y="304" width="0.8" height="20" fill="#2a2a2a" />
              <rect x="49" y="304" width="1.8" height="20" fill="#2a2a2a" />
              <rect x="53" y="304" width="1" height="20" fill="#2a2a2a" />
              <rect x="56" y="304" width="1.4" height="20" fill="#2a2a2a" />
              <rect x="60" y="304" width="1" height="20" fill="#2a2a2a" />
              <rect x="63" y="304" width="2" height="20" fill="#2a2a2a" />
              <rect x="67" y="304" width="1" height="20" fill="#2a2a2a" />

              {/* grain, for a tactile print feel */}
              <rect width="480" height="360" filter="url(#cdhGrain)" opacity={variant.grainOpacity} />

              {/* bleed + spine guides, drawn on top */}
              <rect x="8" y="8" width="464" height="344" fill="none" stroke="#fb923c" strokeWidth={1.4} strokeDasharray="4 3" opacity={0.9} />
              <line x1="228" y1="8" x2="228" y2="352" stroke="#fb923c" strokeWidth={1.4} strokeDasharray="4 3" opacity={0.9} />
              <line x1="252" y1="8" x2="252" y2="352" stroke="#fb923c" strokeWidth={1.4} strokeDasharray="4 3" opacity={0.9} />
            </svg>

            <div
              className="absolute -translate-x-1/2 -translate-y-1/2 text-center"
              style={{ left: '76.25%', top: '34%', width: '40%' }}
            >
              <p
                className="leading-[1.15] mb-2"
                style={{
                  fontFamily: variant.theme === 'geometric' ? 'system-ui, sans-serif' : 'Georgia, "Times New Roman", serif',
                  fontStyle: variant.theme === 'watercolor' || variant.theme === 'cinematic' ? 'italic' : 'normal',
                  fontWeight: variant.theme === 'geometric' ? 700 : variant.theme === 'minimalistic' ? 300 : 400,
                  textTransform: variant.theme === 'geometric' || variant.theme === 'vintage' ? 'uppercase' : 'none',
                  fontSize: 'clamp(11px, 5cqw, 19px)',
                  letterSpacing: variant.theme === 'vintage' ? '0.06em' : variant.theme === 'geometric' ? '0.03em' : '0.02em',
                  color: variant.textColor,
                  textShadow: variant.onLight ? 'none' : '0 1px 6px rgba(0,0,0,0.25)',
                }}
              >
                {variant.titleLines[0]}<br />{variant.titleLines[1]}
              </p>
              <div className="w-6 h-px mx-auto mb-2" style={{ backgroundColor: variant.textColor, opacity: 0.55 }} />
              <p
                className="uppercase"
                style={{
                  fontFamily: variant.theme === 'geometric' ? 'system-ui, sans-serif' : undefined,
                  fontSize: 'clamp(6.5px, 2cqw, 9px)',
                  letterSpacing: '0.14em',
                  color: variant.textColor,
                  opacity: 0.7,
                }}
              >
                {variant.author}
              </p>
            </div>
            <div
              className="absolute left-1/2 top-1/2 uppercase whitespace-nowrap"
              style={{
                fontSize: 'clamp(6px, 1.6cqw, 9px)',
                letterSpacing: '0.1em',
                transform: 'translate(-50%, -50%) rotate(-90deg)',
                color: variant.textColor,
                opacity: 0.55,
              }}
            >
              {variant.titleLines.join(' ')}
            </div>
          </div>
          </button>
        )}

        {showSavedCover ? (
          <div className="flex justify-center mt-2">
            <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10.5px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full">
              Ready to Print
            </span>
          </div>
        ) : (
          <div className="flex justify-center gap-1.5 mt-2">
            {COVER_VARIANTS.map((v, i) => (
              <span
                key={v.theme}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === variantIndex ? 'bg-orange-500 scale-125' : 'bg-[#d6c9a8]'}`}
              />
            ))}
          </div>
        )}
        </div>

      {/* Copy + CTA — below the illustration, full width */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-wide text-orange-600 mb-1">Cover Designer</p>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Design your cover — sized exactly right, automatically
        </h2>
        <p className="text-sm leading-relaxed text-stone-600 mb-4">
          Built to your book's real spine width the moment you open it, so there's no guessing at
          dimensions and nothing gets cropped at print. See the bleed and safe zones as you place every element.
        </p>
        <ul className="flex flex-wrap gap-x-5 gap-y-2.5 mb-5 list-none p-0">
          {heroPoints.map((point) => (
            <li key={point} className="flex items-center gap-1.5 text-[12.5px] font-medium text-gray-700">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#57534e" strokeWidth={2.5} className="shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              {point}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={onStartDesigning}
          className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-sm font-semibold px-5 py-2.5 rounded-lg shadow-[0_2px_6px_rgba(249,115,22,0.3)] hover:bg-orange-600 transition-colors"
        >
          {hasExistingCoverDesign ? 'Continue Designing' : 'Start Designing'}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default CoverDesignerHero;
